import { useContext, useEffect, useRef } from "react";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import useRefsInit from "../hooks/useRefsInit.jsx";
import SwivuSearchBar from "../components/SwivuSearchBar.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import CHECKLIST_GRID_CONFIG from "../../other/constants/ChecklistView/CHECKLIST_GRID_CONFIG.jsx";
import { callOnLoadOnColumns, canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import { showToast, displayPermissionNotAllowed, swivuCheckGridGrants, getOriginalRecord, cellAvatarTooltipConfig, showConfirmationDialog } from "../../other/utils/utils.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { ENTITY_TYPES, GRID_FIELDS_TO_OPEN_EDITOR, VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import useEnableViewTracking from "../hooks/useEnableViewTracking.jsx";
import { exportChecklistItemsApi, importChecklistItemsApi } from "../../network/network.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { MessageDialog } from "@bryntum/schedulerpro";

const ChecklistPage = () => {
  const gridRef = useRef(null);
  const { setRefs, isRefsInit } = useRefsInit([gridRef]);
  const context = useContext(BryntumContext);
  const { boardManager, project, user, editorHandler, currentViewRef, entityManager } = context;

  useEnableViewTracking(gridRef, VIEW_TYPES.checklists, isRefsInit);

  useEffect(() => {
    if (user && project) {
      const checklistInstance = gridRef.current.instance;
      const { exportChecklistItems, importChecklistItem, removeRow, archive } = checklistInstance.features.cellMenu.items;
      checklistInstance.store = project.checklistStore;
      // Removing record deletion on delete key because it could trigger record deletion for those users who are not allowed to delete records
      checklistInstance.keyMap["Delete"].handler = () => {};
      checklistInstance.store.relayAll(gridRef.current.instance, "swivuChecklistStore");
      importChecklistItem.onItem = onOpenImportChecklistItemsPicker;
      const fileField = checklistInstance.bbar.items[0];
      fileField.onChange = onImportChecklistItems;
      exportChecklistItems.onItem = async ({ selection }) => {
        try {
          await Promise.all(selection.map((record) => exportChecklistItemsApi(record.id)));
          showToast({ msg: "Data has been exported successfully.", type: "success" });
        } catch (e) {
          console.log(e);
        }
      };

      removeRow.onItem = () => {
        const selectedRecord = checklistInstance.selectedRecord;
        if (selectedRecord && user.can(checklistInstance.selectedRecord.canEdit)) {
          const selectedIds = checklistInstance.selectedRecords.map((r) => r.id);
          showConfirmationDialog({
            title: `Delete Checklist`,
            message: `<div>Are you sure you want to delete the checklist "${checklistInstance.selectedRecord?.title}"?</div>`,
            cancelBtnText: "Cancel",
            confirmBtnText: "Delete",
            isCloseByHeader: true
          }).then((isDelete) => {
            if (isDelete) {
              checklistInstance.store.remove(selectedIds);
              project.checklistStore.remove(selectedIds);
            }
          });
        } else {
          displayPermissionNotAllowed("delete", boardManager.selectedBoard.record.roleNames);
        }
      };
      archive.onItem = () => {
        if (checklistInstance.selectedRecord) {
          if (user.can(checklistInstance.selectedRecord.canEdit)) {
            checklistInstance.selectedRecord.archived = !checklistInstance.selectedRecord.archived;
          } else {
            displayPermissionNotAllowed("delete", boardManager.selectedBoard.record.roleNames);
          }
        }
      };
      checklistInstance.features.cellMenu.processItems = (e) => {
        processCellMenuItems({ entityName: "Checklist", grid: gridRef.current.instance, user, project })(e);
        e.items.removeRow.hidden = false;
      };
      callOnLoadOnColumns(checklistInstance.columns, project, { context, editorHandler });
      currentViewRef.current.currentViewPref = {
        viewStates: [{ name: "Checklist", store: project.checklistStore, grid: checklistInstance }]
      };
      swivuCheckGridGrants(checklistInstance, user);
    }
  }, [gridRef, project, user, editorHandler, context]);

  const onOpenImportChecklistItemsPicker = () => {
    const checklistInstance = gridRef.current.instance;
    const fileFieldImport = checklistInstance.bbar.items[0];
    if (user.can(checklistInstance.selectedRecord.canEdit)) {
      fileFieldImport.input.click();
    }
  };

  const onImportChecklistItems = (e) => {
    const file = e.source.files?.[0];
    if (file) {
      if (file && file.type === "text/plain") {
        const checklistInstance = gridRef.current.instance;
        const selectedRecord = checklistInstance.selectedRecord;
        const fileReader = new FileReader();
        fileReader.onload = async ({ target: { result: checklistItemsData } }) => {
          try {
            const { data } = await importChecklistItemsApi(selectedRecord.id, checklistItemsData, checklistInstance.mask("Importing 0%"));
            project.applyChangeset({
              [STORE_KEYS.checklistItemStore]: { added: data[STORE_KEYS.checklistItemStore].rows }
            });
          } catch (e) {
            console.log(e);
            MessageDialog.alert({
              title: "Error",
              message: e.message || "Internal Server Error. Please contact support for further assistance."
            });
          } finally {
            e.source.clear();
            e.source.value = null; // clearing this will call this import function again, so make sure to check for file length
            checklistInstance.unmask();
          }
        };
        fileReader.readAsText(file);
      } else {
        MessageDialog.alert({
          title: "Error",
          message: `Unsupported File Type. Please upload files with extensions: .txt}`
        });
      }
    }
  };

  const onOpenEditorByEntityType = ({ record, column }) => {
    if (canOpenEditor(column, record)) {
      if (column.id === "checklist-record-title" || column.isAssignToColumn) {
        const entity = record.entity;
        editorHandler.openEditor(EDITOR_KEYS.checklistEditor, null, {
          entity,
          checklistId: record.id
        }, { by: "recordType", useExisting: true });
      } else if (column.field === GRID_FIELDS_TO_OPEN_EDITOR.ENTITY_NAME) {
        if (record.taskId) {
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: record.entity.id });
        } else if (record.resourceId) {
          entityManager.openEntityManager({ entityType: ENTITY_TYPES.RESOURCE, entityId: record.entity.id });
        } else if (record.clientId) {
          entityManager.openEntityManager({
            entityType: ENTITY_TYPES.CLIENT,
            entityId: record.entity.id,
            otherParams: {
              editChecklist: record.id
            }
          });
        }
      }
    }
  };

  const onBeforeCellEdit = ({ editorContext: { record } }) => {
    const canEdit = user.can(record.canEdit);
    if (!canEdit) displayPermissionNotAllowed("update", boardManager.selectedBoard.record.roleNames);
    return canEdit;
  };

  const onGridCellClick = debounce(({ column, record, event }) => {
    const originalRecord = getOriginalRecord(record);
    if (canOpenEditor(column, originalRecord) && event.detail === 1 && column.isAssignToColumn && user.can(originalRecord.canEdit)) {
      editorHandler.openEditor(
        EDITOR_KEYS.entityTagEditor,
        null,
        {
          entityRecord: originalRecord,
          event,
          column
        },
        { by: "recordType", useExisting: true }
      );
    }
  }, 200);

  return (
    <>
      <SwivuSearchBar searchableRefs={[gridRef]} />
      <BryntumGrid
        emptyText="<p class='text-[#c5c4c4] dark:text-[#7e7e71] font-thin text-center h-full text-lg flex items-center'>To create a checklist, open any Task, Resource, or Client, click the Checklist tab, and then click &nbsp; <Strong> + </strong></p> "
        ref={setRefs(gridRef)}
        {...CHECKLIST_GRID_CONFIG}
        onCellDblClick={onOpenEditorByEntityType}
        onCellClick={onGridCellClick}
        onBeforeCellEditStart={onBeforeCellEdit}
        cellTooltipFeature={cellAvatarTooltipConfig}
        searchFeature={true}
      />
    </>
  );
};

export default ChecklistPage;
